/**
 * Captura de IDs de browser para attribution (Jean 5.3).
 * Não escreve cookies Meta — só lê `_fbp` / `_fbc` / `_ga` e monta `fbc` CAPI.
 * `fbc` construído deve ser reutilizado (storedFbc) para não variar o timestamp a cada load.
 */

const FBP_COOKIE = "_fbp"
const FBC_COOKIE = "_fbc"
const GA_COOKIE = "_ga"

const MAX_ID_LENGTH = 255

/** `fb.{subdomain}.{creationTime}.{random}` — Meta Pixel. */
const FBP_PATTERN = /^fb\.\d+\.\d+\.[A-Za-z0-9._-]+$/

/** `fb.1.{ts_ms}.{fbclid}` — CAPI / cookie `_fbc`. */
const FBC_PATTERN = /^fb\.\d+\.\d+\.[A-Za-z0-9._-]+$/

const GA_CLIENT_ID_PATTERN = /^\d+\.\d+$/

export function readNamedCookieValue(cookieHeader: string, name: string): string | null {
  if (!cookieHeader?.trim() || !name) return null
  const parts = cookieHeader.split(";")
  const prefix = `${name}=`
  for (const part of parts) {
    const trimmed = part.trim()
    if (!trimmed.startsWith(prefix)) continue
    const raw = trimmed.slice(prefix.length)
    try {
      const value = decodeURIComponent(raw).trim()
      return value || null
    } catch {
      return null
    }
  }
  return null
}

export function isPlausibleFbp(value: string | null | undefined): value is string {
  if (typeof value !== "string") return false
  const trimmed = value.trim()
  if (trimmed.length < 8 || trimmed.length > MAX_ID_LENGTH) return false
  return FBP_PATTERN.test(trimmed)
}

export function isPlausibleFbc(value: string | null | undefined): value is string {
  if (typeof value !== "string") return false
  const trimmed = value.trim()
  if (trimmed.length < 8 || trimmed.length > MAX_ID_LENGTH) return false
  return FBC_PATTERN.test(trimmed)
}

export function isPlausibleGaClientId(value: string | null | undefined): value is string {
  if (typeof value !== "string") return false
  const trimmed = value.trim()
  if (!trimmed || trimmed.length > MAX_ID_LENGTH) return false
  return GA_CLIENT_ID_PATTERN.test(trimmed)
}

/** Aceita `XXXXXXXX.YYYYYYYY` persistido ou o cookie `_ga` completo. */
export function normalizeGaClientId(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  if (!trimmed) return null
  if (isPlausibleGaClientId(trimmed)) return trimmed
  return extractGaClientIdFromGaCookie(trimmed)
}

export function extractGaClientIdFromGaCookie(gaCookie: string | null | undefined): string | null {
  if (typeof gaCookie !== "string") return null
  const trimmed = gaCookie.trim()
  if (!trimmed) return null

  const parts = trimmed.split(".")
  if (parts.length < 4) return null

  const clientId = `${parts[parts.length - 2]}.${parts[parts.length - 1]}`
  return isPlausibleGaClientId(clientId) ? clientId : null
}

export function buildFbcFromFbclid(fbclid: string, timestampMs: number): string | null {
  const id = fbclid.trim()
  if (!id) return null
  if (!Number.isFinite(timestampMs) || timestampMs <= 0) return null
  const ts = Math.trunc(timestampMs)
  const fbc = `fb.1.${ts}.${id}`
  return isPlausibleFbc(fbc) ? fbc : null
}

/** `fb.{sub}.{ts}.{fbclid}` — o fbclid é o sufixo após o 3º ponto. */
export function fbcMatchesFbclid(fbc: string, fbclid: string | null | undefined): boolean {
  const clickId = fbclid?.trim()
  if (!clickId) return true
  return fbc.endsWith(`.${clickId}`)
}

export function pickStableFbc(args: {
  cookieFbc?: string | null
  storedFbc?: string | null
  fbclid?: string | null
  nowMs?: number
}): string | null {
  const cookie = isPlausibleFbc(args.cookieFbc) ? args.cookieFbc.trim() : null
  if (cookie && fbcMatchesFbclid(cookie, args.fbclid)) return cookie

  const stored = isPlausibleFbc(args.storedFbc) ? args.storedFbc.trim() : null
  if (stored && fbcMatchesFbclid(stored, args.fbclid)) return stored

  if (args.fbclid && args.nowMs != null) {
    return buildFbcFromFbclid(args.fbclid, args.nowMs)
  }

  return null
}

export interface BrowserAttributionIds {
  fbp: string | null
  fbc: string | null
  gaClientId: string | null
}

/**
 * Lê cookies 1st-party e monta `fbc` no formato CAPI quando há `fbclid`.
 * Prefere `_fbc` do Pixel, depois `storedFbc` (mesmo fbclid).
 * Só constrói `fb.1.<ts_ms>.<fbclid>` quando `nowMs` é o horário do clique.
 */
export function captureBrowserAttributionIds(args: {
  cookieHeader: string
  fbclid?: string | null
  storedFbc?: string | null
  nowMs?: number
}): BrowserAttributionIds {
  const fbpRaw = readNamedCookieValue(args.cookieHeader, FBP_COOKIE)
  const fbcCookie = readNamedCookieValue(args.cookieHeader, FBC_COOKIE)
  const gaCookie = readNamedCookieValue(args.cookieHeader, GA_COOKIE)

  const fbp = isPlausibleFbp(fbpRaw) ? fbpRaw.trim() : null

  return {
    fbp,
    fbc: pickStableFbc({
      cookieFbc: fbcCookie,
      storedFbc: args.storedFbc,
      fbclid: args.fbclid,
      nowMs: args.nowMs,
    }),
    gaClientId: extractGaClientIdFromGaCookie(gaCookie),
  }
}
